import { useEffect, useState } from "react";
import type { FormEvent } from "react";

const FORM_SUBMIT_ADDR = "/api/solicitar-visita";

type Status = "idle" | "loading" | "success" | "error";

const inputClass =
  "w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors";

const labelClass = "block text-sm font-semibold text-foreground-800 mb-2";

export default function HomeVisitRequest() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeModal = () => {
    setOpen(false);
    setStatus("idle");
    setFormError("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "loading") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload[key] = value.trim();
    });

    try {
      setStatus("loading");
      setFormError("");
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      let parsed: { ok?: boolean; message?: string } | null = null;
      try {
        parsed = await response.json();
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          parsed?.message?.trim()
            ? parsed.message
            : "No pudimos enviar tu solicitud. Intenta de nuevo o llámanos por teléfono.",
        );
      }
    } catch {
      setStatus("error");
      setFormError("Hubo un problema de conexión. Revisa tu internet e inténtalo otra vez.");
    }
  };

  return (
    <>
      {/* Botón fijo */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Solicitar visita a domicilio"
        className="fixed z-50 left-4 md:left-6 bottom-4 md:bottom-6 flex items-center justify-center md:justify-start gap-2 w-14 h-14 md:w-auto md:h-auto rounded-full bg-accent-500 text-background-50 py-0 md:py-3 md:pl-4 md:pr-5 shadow-lg hover:bg-accent-600 transition-colors duration-300 cursor-pointer active:scale-95"
      >
        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/20 flex-shrink-0">
          <i className="ri-home-smile-2-line text-lg"></i>
        </span>
        <span className="hidden md:block text-sm font-semibold whitespace-nowrap">
          Solicitar visita a domicilio
        </span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-foreground-950/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Solicitar visita a domicilio"
        >
          <div
            className="bg-background-50 w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-lg sm:rounded-lg border border-background-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 p-6 md:p-7 border-b border-background-200 sticky top-0 bg-background-50 z-10">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 flex-shrink-0">
                  <i className="ri-home-smile-2-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-tight">
                    Solicitar visita a domicilio
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 mt-0.5">
                    Llevamos el estudio hasta tu hogar
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Cerrar formulario"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {status === "success" ? (
              <div className="p-8 md:p-10 text-center">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-5">
                  <i className="ri-checkbox-circle-fill text-4xl"></i>
                </span>
                <h4 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
                  ¡Solicitud enviada!
                </h4>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-sm mx-auto">
                  Gracias por confiar en ANEDAN. Te contactaremos muy pronto para confirmar tu visita a domicilio y
                  resolver cualquier duda.
                </p>
                <button
                  type="button"
                  onClick={closeModal}
                  className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form
                id="solicitud-visita-domicilio"
                onSubmit={handleSubmit}
                className="p-6 md:p-7 flex flex-col gap-5"
              >
                <div>
                  <label htmlFor="hv-name" className={labelClass}>
                    Nombre completo <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="hv-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Ej. María López Hernández"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="hv-phone" className={labelClass}>
                      Teléfono / WhatsApp <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="hv-phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      placeholder="55 0000 0000"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="hv-email" className={labelClass}>
                      Correo electrónico
                    </label>
                    <input
                      id="hv-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="tucorreo@ejemplo.com"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="hv-service" className={labelClass}>
                    Servicio que necesitas <span className="text-accent-600">*</span>
                  </label>
                  <select id="hv-service" name="service" required defaultValue="" className={`${inputClass} cursor-pointer`}>
                    <option value="" disabled>
                      Selecciona una opción
                    </option>
                    <option value="Rayos X">Rayos X</option>
                    <option value="Análisis clínicos">Análisis clínicos</option>
                    <option value="Electrocardiogramas">Electrocardiogramas</option>
                    <option value="Aún no estoy seguro">Aún no estoy seguro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="hv-address" className={labelClass}>
                    Dirección para la visita <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="hv-address"
                    name="address"
                    type="text"
                    required
                    autoComplete="street-address"
                    placeholder="Calle, número, colonia y referencias"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="hv-message" className={labelClass}>
                    Cuéntanos más (opcional)
                  </label>
                  <textarea
                    id="hv-message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Horario en que te acomoda, si tienes orden médica, estudio especifico a realizar, o algún detalle que podamos saber"
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                {status === "error" && formError && (
                  <div className="flex items-start gap-3 rounded-md bg-accent-100 border border-accent-300 px-4 py-3">
                    <i className="ri-error-warning-line text-accent-700 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-accent-900 leading-snug">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base"></i>
                      Enviar solicitud
                    </>
                  )}
                </button>

                <p className="text-xs text-foreground-500 text-center leading-relaxed">
                  Al enviar aceptas que te contactemos para coordinar tu visita. También puedes llamarnos al
                  55 3006 2938.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}